import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { Search, X, PackageCheck, Layers, Layout, ArrowRight } from 'lucide-react';
import { SAAS_PRODUCTS, SERVICES } from '../data/siteData';
import { useBlogData } from '../hooks/useBlogData';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const { data: blogData } = useBlogData();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Handle Cmd+K to open, Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        // The event to open is handled in Header, but we can prevent default here if needed
      }
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const searchQuery = query.toLowerCase().trim();

  const filteredProducts = SAAS_PRODUCTS.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery) ||
      p.description.toLowerCase().includes(searchQuery)
  );

  const filteredServices = SERVICES.filter(
    (s) =>
      s.title.toLowerCase().includes(searchQuery) ||
      s.shortDesc.toLowerCase().includes(searchQuery)
  );

  const filteredPosts = (blogData?.posts || []).filter(
    (p) =>
      p.title.toLowerCase().includes(searchQuery) ||
      p.content.toLowerCase().includes(searchQuery) ||
      p.labels.some((l) => l.toLowerCase().includes(searchQuery))
  );

  const hasResults =
    filteredProducts.length > 0 ||
    filteredServices.length > 0 ||
    filteredPosts.length > 0;

  const handleNavigate = (path: string) => {
    onClose();
    setQuery('');
    navigate(path);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4 pb-20">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh]"
          >
            {/* Search Input Area */}
            <div className="relative border-b border-slate-100 dark:border-slate-800 p-4 flex items-center">
              <Search className="w-5 h-5 text-slate-400 absolute left-6" />
              <input
                ref={inputRef}
                type="text"
                className="w-full bg-transparent border-none focus:ring-0 pl-10 pr-12 py-2 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-lg outline-none"
                placeholder="Search products, services, or insights..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button
                onClick={onClose}
                className="absolute right-4 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Results Area */}
            <div className="flex-1 overflow-y-auto p-2">
              {query === '' ? (
                <div className="p-10 text-center text-slate-500 dark:text-slate-400 space-y-3">
                  <Search className="w-10 h-10 mx-auto opacity-20" />
                  <p>Start typing to search across the platform.</p>
                </div>
              ) : !hasResults ? (
                <div className="p-10 text-center text-slate-500 dark:text-slate-400 space-y-3">
                  <p>No results found for "{query}".</p>
                </div>
              ) : (
                <div className="space-y-4 p-2">
                  {/* Products */}
                  {filteredProducts.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 px-3">
                        Products
                      </h3>
                      <ul className="space-y-1">
                        {filteredProducts.slice(0, 3).map((product) => (
                          <li key={product.id}>
                            <button
                              onClick={() => handleNavigate(`/products/${product.id}`)}
                              className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors group text-left"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center text-sky-600 dark:text-sky-400 shrink-0">
                                  <PackageCheck className="w-4 h-4" />
                                </div>
                                <div>
                                  <div className="font-semibold text-slate-900 dark:text-white group-hover:text-royal-blue transition-colors">
                                    {product.name}
                                  </div>
                                  <div className="text-xs text-slate-500 line-clamp-1">
                                    {product.description}
                                  </div>
                                </div>
                              </div>
                              <ArrowRight className="w-4 h-4 text-slate-300 dark:text-slate-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Services */}
                  {filteredServices.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 px-3 pt-2">
                        Services
                      </h3>
                      <ul className="space-y-1">
                        {filteredServices.slice(0, 3).map((service) => (
                          <li key={service.id}>
                            <button
                              onClick={() => handleNavigate(`/services/${service.id}`)}
                              className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors group text-left"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                                  <Layers className="w-4 h-4" />
                                </div>
                                <div>
                                  <div className="font-semibold text-slate-900 dark:text-white group-hover:text-royal-blue transition-colors">
                                    {service.title}
                                  </div>
                                  <div className="text-xs text-slate-500 line-clamp-1">
                                    {service.shortDesc}
                                  </div>
                                </div>
                              </div>
                              <ArrowRight className="w-4 h-4 text-slate-300 dark:text-slate-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Blog Posts */}
                  {filteredPosts.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 px-3 pt-2">
                        Insights & Blog
                      </h3>
                      <ul className="space-y-1">
                        {filteredPosts.slice(0, 3).map((post) => (
                          <li key={post.id}>
                            <button
                              onClick={() => handleNavigate(`/blog/${post.slug}`)}
                              className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors group text-left"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                                  <Layout className="w-4 h-4" />
                                </div>
                                <div>
                                  <div className="font-semibold text-slate-900 dark:text-white group-hover:text-royal-blue transition-colors line-clamp-1">
                                    {post.title}
                                  </div>
                                  <div className="flex gap-2 mt-1">
                                    {post.labels.slice(0, 2).map((label, idx) => (
                                      <span key={idx} className="text-[9px] uppercase tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded">
                                        {label}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <ArrowRight className="w-4 h-4 text-slate-300 dark:text-slate-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-100 dark:border-slate-800 p-3 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-between text-xs text-slate-500">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1"><kbd className="bg-slate-200 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono text-[10px]">↑</kbd> <kbd className="bg-slate-200 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono text-[10px]">↓</kbd> to navigate</span>
                <span className="flex items-center gap-1"><kbd className="bg-slate-200 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono text-[10px]">Enter</kbd> to select</span>
              </div>
              <span className="flex items-center gap-1"><kbd className="bg-slate-200 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono text-[10px]">Esc</kbd> to close</span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
